import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { couponsPathTo } from '../utils/Api';
import { claimPendingCouponWithFeedback, setPendingCouponCode } from '../utils/coupons';
import { UserContext } from '../contexts/UserContext';
import SnackbarContext from '../contexts/SnackbarContext';
import { currency } from './building_blocks/Currency';
import TudoPetsLogo from '../images/tp_logo_laranja.png';
import FormBox from './building_blocks/FormBox';

const availabilityCopy = {
  expired: 'Este cupom expirou.',
  exhausted: 'Este cupom esgotou.',
  not_found: 'Cupom não encontrado.',
};

const CouponLanding = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const { openSnackbar } = useContext(SnackbarContext);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const redeemStarted = useRef(false);

  useEffect(() => {
    document.title = 'Cupom - TudoPets.app';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const normalized = (code || '').toUpperCase();

    couponsPathTo
      .publicShow(normalized)
      .then(async (result) => {
        if (cancelled) return;
        const nextAvailability = result.data.availability;
        const nextCoupon = result.data.coupon;
        setAvailability(nextAvailability);
        setCoupon(nextCoupon);

        if (nextAvailability !== 'ok' || !nextCoupon?.code) return;

        setPendingCouponCode(nextCoupon.code);

        if (currentUser && !redeemStarted.current) {
          redeemStarted.current = true;
          setRedeeming(true);
          await claimPendingCouponWithFeedback(openSnackbar);
          if (!cancelled) navigate('/home');
        }
      })
      .catch((error) => {
        if (cancelled) return;
        const nextAvailability = error?.response?.data?.availability || 'not_found';
        setAvailability(nextAvailability);
        setCoupon(error?.response?.data?.coupon || null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [code, currentUser, navigate, openSnackbar]);

  const invalidMessage = availability && availability !== 'ok' ? availabilityCopy[availability] : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, pt: 2 }}>
      <Box component="img" src={TudoPetsLogo} alt="tudopets.app" sx={{ width: 160, maxWidth: '70%' }} />

      <FormBox>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          {loading || redeeming ? (
            <Typography>
              {redeeming ? 'Resgatando cupom...' : 'Carregando cupom...'}
            </Typography>
          ) : invalidMessage ? (
            <>
              <Typography variant="h5" color="primary">
                {invalidMessage}
              </Typography>
              <Typography>
                Peça na pet shop do seu bairro e receba em casa no tudopets.app.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/home')}>
                Ir para o tudopets.app
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" color="primary">
                {coupon?.discount_value
                  ? `${currency(coupon.discount_value)} de desconto na sua próxima compra`
                  : 'Cupom de desconto'}
              </Typography>
              {coupon?.name && (
                <Typography sx={{ fontWeight: 700 }}>{coupon.name}</Typography>
              )}
              {coupon?.expires_at && (
                <Typography variant="body2" color="text.secondary">
                  Válido até{' '}
                  {new Date(coupon.expires_at).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              )}
              <Typography>
                Peça na pet shop do seu bairro e receba em casa. Cadastre-se no tudopets.app para resgatar este cupom.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                <Button variant="contained" fullWidth onClick={() => navigate('/register')}>
                  Cadastrar
                </Button>
                <Button variant="outlined" fullWidth onClick={() => navigate('/login')}>
                  Entrar
                </Button>
              </Box>
            </>
          )}
        </Box>
      </FormBox>
    </Box>
  );
};

export default CouponLanding;
